import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import "./Common/UnitsListViewComponent.css";
import { ALERT_VIDEOS } from "../utils/constants";
import Button from "./Common/Button";
import AlertIcon from "./Common/AlertIcon";
import ConnectionStatusIcon from "./Common/ConnectionStatusIcon";

const getAlertVideoUrl = (alertType) => {
  const normalizedType = alertType?.toLowerCase().trim() || "";
  if (normalizedType === "air filter") {
    return ALERT_VIDEOS.AIR_FILTER_VIDEO_URL;
  }
  if (normalizedType.startsWith("condensate")) {
    return ALERT_VIDEOS.CONDENSATE_VIDEO_URL;
  }
  return null;
};

const AlarmList = ({ alarm, timeZone, navigationState }) => {
  const navigate = useNavigate();
  const [columnSizing, setColumnSizing] = useState(() => {
    try {
      const stored = localStorage.getItem("alarmsListColumnWidths");
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      return {};
    }
  });

  const handleColumnSizingChange = useCallback((updater) => {
    setColumnSizing((previous) => {
      const updated = typeof updater === "function" ? updater(previous) : updater;
      try {
        localStorage.setItem("alarmsListColumnWidths", JSON.stringify(updated));
      } catch (error) {
        // ignore write failures
      }
      return updated;
    });
  }, []);

  const rows = useMemo(
    () =>
      (alarm || []).map((item, index) => ({
        ...item,
        rowId: item?.id || `${item?.unit?.serialNumber || "row"}-${index}`,
      })),
    [alarm]
  );

  const columns = useMemo(
    () => [
      {
        id: "unitName",
        header: "Unit Name",
        size: 220,
        enableResizing: true,
        cell: ({ row }) => {
          const serialNumber = row.original?.unit?.serialNumber;
          const name = row.original?.unit?.metadata?.name || "--";
          return (
            <p className="truncate max-w-60" title={row.original?.unit?.metadata?.name} data-testid={`alarm-list-name-${row.id}`}>
              {serialNumber ? (
                <Link
                  to={`/units/${serialNumber}`}
                  state={navigationState}
                  onClick={(event) => event.stopPropagation()}
                  data-testid={`alarm-list-name-link-${row.id}`}
                >
                  {name}
                </Link>
              ) : (
                name
              )}
            </p>
          );
        },
      },
      {
        id: "serialNumber",
        header: "Serial Number",
        size: 180,
        enableResizing: true,
        cell: ({ row }) => <span data-testid={`alarm-list-serial-${row.id}`}>{row.original?.unit?.serialNumber || "--"}</span>,
      },
      {
        id: "connectionStatus",
        header: "Connection",
        size: 130,
        enableResizing: true,
        cell: ({ row }) => (
          <ConnectionStatusIcon
            connected={row.original?.unit?.connected === true}
            connectionType={row.original?.unit?.connectionType}
            className="size-6"
            hasActiveAlert={row.original?.resolvedAt == null}
            data-testid={`connection-status-${row.original?.rowId}`}
          />
        ),
      },
      {
        id: "description",
        header: "Description",
        size: 340,
        enableResizing: true,
        cell: ({ row }) => {
          const type = row.original?.type;
          const videoUrl = getAlertVideoUrl(type);
          return (
            <div className="flex items-center justify-between gap-3">
              <p className="whitespace-break-spaces line-clamp-4 flex-1" data-testid={`alarm-list-message-${row.id}`}>
                {row.original?.message}
              </p>
              {videoUrl && (
                <div className="flex shrink-0 self-center pl-3">
                  <Button
                    className="inline-flex items-center justify-center text-theme hover:text-theme/80 transition-colors duration-200"
                    onClick={(event) => {
                      event.stopPropagation();
                      window.open(videoUrl, "_blank", "noopener,noreferrer");
                    }}
                    aria-label="Watch instructional video"
                    title="Watch instructional video"
                    data-testid={`alarm-list-video-button-${row.id}`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="32"
                      height="32"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <circle cx="8" cy="8" r="7.25" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M6.5 5.75L10.25 8L6.5 10.25V5.75Z" fill="currentColor" />
                    </svg>
                  </Button>
                </div>
              )}
            </div>
          );
        },
      },
      {
        id: "dateTime",
        header: "Date & Time",
        size: 200,
        enableResizing: true,
        cell: ({ row }) => (
          <span data-testid={`alarm-list-datetime-${row.id}`}>
            {new Date(row.original?.startedAt).toLocaleDateString("en-US", {
              timeZone,
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })}
            <br />
            {new Date(row.original?.startedAt).toLocaleTimeString("en-GB", {
              timeZone,
              hour12: false,
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })}
          </span>
        ),
      },
      {
        id: "type",
        header: "Type",
        size: 180,
        enableResizing: true,
        cell: ({ row }) => (
          <span className={`text-${row.original.severity}`} data-testid={`alarm-list-type-${row.id}`}>
            {row.original.type || "--"}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        size: 180,
        enableResizing: true,
        cell: ({ row }) => {
          const severity = row.original.severity;
          return (
            <div
              className={`flex items-center gap-2 px-2.5 py-1 rounded-[50px] w-fit bg-${severity}`}
              data-testid={`alarm-list-severity-${row.id}`}
            >
              <div className="iconHolder shrink-0">
                <AlertIcon severity={severity} strokeWhite />
              </div>
              <span className="text-white text-xs leading-5 lg:text-sm lg:leading-6 font-medium tracking-[0.021px] grow-0">
                {severity.charAt(0).toUpperCase() + severity.slice(1)}
              </span>
            </div>
          );
        },
      },
    ],
    [timeZone, navigationState]
  );

  const gridHeight = useMemo(() => {
    const HEADER_HEIGHT = 54;
    const ROW_HEIGHT = 76;
    const BORDER_ALLOWANCE = 2;
    const calculatedHeight = HEADER_HEIGHT + (rows.length || 1) * ROW_HEIGHT + BORDER_ALLOWANCE;
    return Math.max(360, Math.min(900, calculatedHeight));
  }, [rows.length]);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: { columnSizing },
    onColumnSizingChange: handleColumnSizingChange,
    columnResizeMode: "onChange",
    getRowId: (row) => row.rowId,
  });

  return (
    <div className="mt-4 unitsListHolder rounded-md scrollVisible overflow-x-auto w-full" data-testid="alarm-list">
      <div className="units-data-grid" style={{ height: gridHeight, width: "100%" }}>
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} style={{ height: 54 }}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="units-table-header-cell"
                    style={{ width: header.getSize() }}
                  >
                    <div className="units-table-header-content">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </div>
                    {header.column.getCanResize() && (
                      <div
                        className={`column-resizer ${header.column.getIsResizing() ? "is-resizing" : ""}`}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          header.getResizeHandler()(event);
                        }}
                        onTouchStart={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          header.getResizeHandler()(event);
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="units-table-row cursor-pointer"
                style={{ height: 76 }}
                data-testid={`alarm-list-row-${row.id}`}
                onClick={() => {
                  const serialNumber = row.original?.unit?.serialNumber;
                  if (!serialNumber) return;
                  navigate(`/units/${serialNumber}`, { state: navigationState });
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="units-table-cell"
                    style={{ width: cell.column.getSize() }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AlarmList;
